/**
 * Propose a role — the write half of the proposals feature.
 *
 * Files rows in the proposals Airtable table with `Status = Proposed`, which the timeline then
 * renders in purple (see `OrgChartTable`'s `OrgLeafRow`). It never writes to `hiring_plan`: the
 * proposals table is a test environment first, and approval is a separate human flow that does
 * not exist yet (David, Sep 2026).
 *
 * TWO WAYS OUT of this modal (David, Sep 2026):
 *
 *   - **Propose role** files the one role on screen.
 *   - **Propose in bulk** keeps every field except `Track and Level` and the planned start date,
 *     and asks for those per role in a second step (`BulkProposalStep`). "We need four of these,
 *     staggered" was otherwise the same eight fields typed out four times.
 *
 * The hiring-manager picker is built from the org tree already on the page (`hiringManagerOptions`
 * in `proposeRole.ts`) rather than from an endpoint of its own, so a proposal can only ever name
 * a manager the tree is able to place it under.
 *
 * ⚠️ Native `<select>` / `<input>`, not the design-system atoms — the same documented deviation
 * as the TA Cockpit's Role Prioritisation controls, for the same reason: with no `label` prop the
 * DS `Input` renders borderless at 16px and a passed `className` REPLACES its layout classes
 * rather than merging, so it cannot be used as a plain labelled field in a dense grid. `Modal`,
 * `Button` and the toggle's buttons are the design-system ones.
 *
 * BACKFILL MODE (`backfill` prop, from the Org Planning Overview's "Manage backfill"): the same
 * form, pre-filled from the leaver's Personio record (`backfillForm.ts`), Reason for Hiring locked
 * to Backfill, no bulk path (one leaver, one role), and a note on the preselected level.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  ProposalOptionsUnavailableError,
  ProposalValidationError,
  createProposedRole,
  type NotifyOutcome,
  fetchProposalFieldOptions,
  type BulkProposalRow,
  type ProposedRoleInput,
  type SharedProposalInput,
} from '../../services/internalPlaygroundApi';
import { CompensationSummary } from '../../components/CompensationSummary';
import { useRoleCompensation } from './useRoleCompensation';
import { FIELD_CLASS, blankSharedFields, hiringManagerOptions, unitOptionsForDepartment } from './proposeRole';
import { BulkProposalStep } from './BulkProposalStep';
import { ChoiceToggle, Field } from './proposeRoleUi';
import { PriorityField } from './PriorityField';
import { type BackfillTarget, backfillPrefill, fillBlanks, formatDay, levelNote } from './backfillForm';
import type { OrgNode } from '../../services/internalPlaygroundApi';
import { useViewerScope } from './viewerScope';

const EMPTY: ProposedRoleInput = {
  title: '',
  role: '',
  peopleTeam: '',
  hiringManagerEmployeeId: '',
  plannedStartDate: '',
  proposalRational: '',
  unit: '',
  trackAndLevel: '',
  reasonForHiring: '',
  costCategory: '',
  priorityRating: '',
};

/** Everything a bulk submission sends once. The two it leaves out are the two the bulk step asks
 * for per role. */
function sharedFieldsOf(form: ProposedRoleInput): SharedProposalInput {
  const { plannedStartDate: _date, trackAndLevel: _track, ...shared } = form;
  return shared;
}

export function ProposeRoleModal({
  root,
  onClose,
  onProposed,
  backfill,
}: {
  root: OrgNode;
  /** Set when proposing the backfill of a leaver — see BACKFILL MODE above. */
  backfill?: BackfillTarget;
  onClose: () => void;
  /**
   * Called with the Slack outcome just before the modal closes, so the PAGE can warn that the
   * approvers were not told. It cannot be shown in here: the submission succeeded, so the modal is
   * closing, and a warning that disappears with it is one nobody reads.
   */
  onProposed?: (notify: NotifyOutcome, priorityDropped?: boolean) => void;
}) {
  const scope = useViewerScope();
  // A proposer may only name themselves or someone in their reporting line; the proxy refuses
  // anyone else, so the picker never offers them.
  const managers = useMemo(
    () => hiringManagerOptions(root).filter((option) => scope.inLine(option.employeeId)),
    [root, scope]
  );
  const [form, setForm] = useState<ProposedRoleInput>(() =>
    backfill ? backfillPrefill(backfill, undefined, managers).form : EMPTY
  );
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ProposedRoleInput, string>>>({});
  const [bulk, setBulk] = useState(false);
  /**
   * The bulk rows live HERE, not in `BulkProposalStep`, because that component is conditionally
   * rendered and would lose them on every "Back to the shared fields" — which is exactly what the
   * shared-error banner asks someone to do once the rows are already filled. `null` means the bulk
   * step has never been opened; it is seeded from step one's own track and date on first entry.
   */
  const [bulkRows, setBulkRows] = useState<BulkProposalRow[] | null>(null);
  const queryClient = useQueryClient();


  /**
   * What the role on screen would cost, refreshed as the four fields it depends on change.
   *
   * Memoised for the render below rather than for the query: react-query hashes its key
   * structurally, so a fresh array literal would NOT have refetched in a loop.
   */
  const pricedRows = useMemo(
    () => [{ trackAndLevel: form.trackAndLevel, plannedStartDate: form.plannedStartDate }],
    [form.trackAndLevel, form.plannedStartDate]
  );
  const compensation = useRoleCompensation(form.role, form.peopleTeam, pricedRows);

  const optionsQuery = useQuery({
    queryKey: ['internal-playground-proposal-options'],
    queryFn: fetchProposalFieldOptions,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: createProposedRole,
    onSuccess: (result) => {
      // The timeline is what the proposal is FOR, so it is refetched rather than left showing a
      // tree the new purple row is missing from.
      void queryClient.invalidateQueries({ queryKey: ['internal-playground-hiring-timeline'] });
      onProposed?.(result.notify, result.priorityDropped);
      onClose();
    },
    onError: (error) => {
      // A 400 marks the offending fields; anything else is shown as a single message below, since
      // there is no field to attach it to.
      setFieldErrors(error instanceof ProposalValidationError ? error.details : {});
    },
  });

  const set = (key: keyof ProposedRoleInput) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const optionsUnavailable = optionsQuery.error instanceof ProposalOptionsUnavailableError;
  const options = optionsQuery.data;

  // The vocabulary half of a backfill's pre-fill can only be matched once the Airtable choices have
  // loaded. Applied during render (React's "adjust state when an input changes" pattern) rather than
  // in an effect, and only into fields still blank, so nothing typed meanwhile is overwritten.
  const [prefilledFrom, setPrefilledFrom] = useState<typeof options>(undefined);
  if (backfill && options && prefilledFrom !== options) {
    setPrefilledFrom(options);
    setForm((prev) => fillBlanks(prev, backfillPrefill(backfill, options, managers).form));
  }
  const unmatched = useMemo(
    () => (backfill && options ? backfillPrefill(backfill, options, managers).unmatched : []),
    [backfill, options, managers]
  );
  // Role and People Team are required AND can only be filled from the Airtable vocabularies, so
  // with no options there is no submission that can succeed — the proxy would answer 400 marking
  // two fields the person has no way to fill, and the form would loop. Blocked here instead, with
  // the banner above saying why.
  const blocked = !options && !optionsQuery.isPending;

  /** Step one's own gate in front of the bulk step: only emptiness, never the real rules — the
   * proxy still validates everything. Filling twelve rows of dates and then being told the title
   * is missing is the outcome this avoids. */
  const goBulk = () => {
    const missing = blankSharedFields(form);
    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      return;
    }
    setBulkRows((prev) => {
      if (prev) return prev;
      const seed = { trackAndLevel: form.trackAndLevel, plannedStartDate: form.plannedStartDate };
      return [{ ...seed }, { ...seed }];
    });
    setBulk(true);
  };

  // Narrowed to the selected Department — see `unitOptionsForDepartment`'s own doc for why this
  // falls back to the full list (no department yet, or Tech, which legitimately spans every unit).
  const unitOptions = useMemo(
    () => unitOptionsForDepartment(options?.unit ?? [], form.peopleTeam),
    [options, form.peopleTeam]
  );

  const selectField = (key: 'role' | 'peopleTeam' | 'unit' | 'trackAndLevel') => {
    const choices = key === 'unit' ? unitOptions : options?.[key] ?? [];
    const onChange = (value: string) => {
      if (key === 'peopleTeam') {
        // Narrowing Unit to the new Department can leave the current selection unpickable — drop
        // it rather than silently submit a value the person can no longer see chosen.
        const nextUnits = unitOptionsForDepartment(options?.unit ?? [], value);
        setForm((prev) => ({
          ...prev,
          peopleTeam: value,
          unit: prev.unit && nextUnits.includes(prev.unit) ? prev.unit : '',
        }));
        setFieldErrors((prev) => (prev.peopleTeam ? { ...prev, peopleTeam: undefined } : prev));
        return;
      }
      set(key)(value);
    };
    return (
      <select
        id={key}
        className={FIELD_CLASS}
        value={form[key] ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={!options}
      >
        <option value="">{options ? '—' : optionsQuery.isPending ? 'Loading…' : 'Unavailable'}</option>
        {choices.map((choice) => (
          <option key={choice} value={choice}>
            {choice}
          </option>
        ))}
      </select>
    );
  };

  const toggleField = (key: 'reasonForHiring' | 'costCategory') => (
    <ChoiceToggle
      labelledBy={`${key}-label`}
      options={options?.[key] ?? []}
      value={form[key] ?? ''}
      onChange={set(key)}
      // A backfill is a backfill; the proxy forces it as well.
      disabled={!options || (Boolean(backfill) && key === 'reasonForHiring')}
    />
  );

  const summary = [
    { label: 'Title', value: form.title },
    { label: 'Role', value: form.role },
    { label: 'Department', value: form.peopleTeam },
    {
      label: 'Hiring manager',
      value: managers.find((m) => m.employeeId === form.hiringManagerEmployeeId)?.name ?? '',
    },
    { label: 'Unit', value: form.unit ?? '' },
    { label: 'Reason for hiring', value: form.reasonForHiring ?? '' },
    { label: 'Cost category', value: form.costCategory ?? '' },
    { label: 'Suggested priority', value: form.priorityRating ?? '' },
    // The longest free-text field, and the one actually worth re-reading before committing twenty
    // rows — omitting it was the one thing the recap could not afford to leave out.
    { label: 'Why this role?', value: form.proposalRational, wide: true },
  ];

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,40rem)] gap-4 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-xl !font-semibold">
            {bulk ? 'Propose several roles' : backfill ? `Propose a backfill for ${backfill.name}` : 'Propose a role'}
          </ModalTitle>
        </ModalHeader>

        <p className="text-sm text-zinc-500">
          {bulk
            ? 'Each row below is filed as its own Proposed role, sharing everything you entered on the previous step.'
            : 'Files the role as Proposed in the hiring plan proposals table. It appears on this timeline in purple and is counted apart from committed FTE. Nothing is written to the hiring plan itself.'}
        </p>

        {backfill && (
          <div className="space-y-1 rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
            <p>
              Pre-filled from {backfill.name}&apos;s Personio record
              {backfill.terminationDate ? ` (contract ends ${formatDay(backfill.terminationDate)})` : ''}. Check
              every field before proposing.
            </p>
            {unmatched.length > 0 && (
              <p>Not in the Airtable lists, so left empty: {unmatched.join(', ')}.</p>
            )}
            {fieldErrors.backfillForEmployeeId && (
              <p className="font-medium text-red-700">{fieldErrors.backfillForEmployeeId}</p>
            )}
          </div>
        )}

        {blocked && !bulk && (
          <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {optionsUnavailable
                ? (optionsQuery.error as Error).message
                : 'The role and department options could not be loaded from Airtable, so a proposal cannot be filed right now.'}
            </span>
          </div>
        )}

        {bulk ? (
          <BulkProposalStep
            shared={sharedFieldsOf(form)}
            summary={summary}
            rows={bulkRows ?? []}
            onRowsChange={setBulkRows}
            trackOptions={options?.trackAndLevel ?? []}
            onBack={() => setBulk(false)}
            onClose={onClose}
            onProposed={onProposed}
          />
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              // `loading` on the DS Button only blocks the mouse; Enter in a text field still
              // submits. One role rather than twenty-five, but a duplicate either way.
              if (mutation.isPending) return;
              mutation.mutate(form);
            }}
          >
            <Field label="Title" htmlFor="title" required error={fieldErrors.title}>
              <input
                id="title"
                className={FIELD_CLASS}
                value={form.title}
                onChange={(e) => set('title')(e.target.value)}
                placeholder="Senior Data Analyst - Operations"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Role" htmlFor="role" required error={fieldErrors.role}>
                {selectField('role')}
              </Field>
              {/* The Airtable column is still `People Team`; the label follows what the timeline
                  actually places a role by, which is the reporting line. */}
              <Field
                label="Department (Reporting Line)"
                htmlFor="peopleTeam"
                required
                error={fieldErrors.peopleTeam}
              >
                {selectField('peopleTeam')}
              </Field>
              <Field
                label="Hiring manager"
                htmlFor="hiringManagerEmployeeId"
                required
                error={fieldErrors.hiringManagerEmployeeId}
              >
                <select
                  id="hiringManagerEmployeeId"
                  className={FIELD_CLASS}
                  value={form.hiringManagerEmployeeId}
                  onChange={(e) => set('hiringManagerEmployeeId')(e.target.value)}
                >
                  <option value="">—</option>
                  {managers.map((m) => (
                    <option key={m.employeeId} value={m.employeeId}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Planned start date"
                htmlFor="plannedStartDate"
                required
                error={fieldErrors.plannedStartDate}
              >
                <input
                  id="plannedStartDate"
                  type="date"
                  className={FIELD_CLASS}
                  value={form.plannedStartDate}
                  onChange={(e) => set('plannedStartDate')(e.target.value)}
                />
              </Field>
              <Field label="Unit" htmlFor="unit" error={fieldErrors.unit}>
                {selectField('unit')}
              </Field>
              <Field label="Track and level" htmlFor="trackAndLevel" error={fieldErrors.trackAndLevel}>
                {selectField('trackAndLevel')}
              </Field>
              {/* Two choices each, so both are on screen instead of behind a menu — see
                  `ChoiceToggle`. */}
              <Field
                label="Reason for hiring"
                labelId="reasonForHiring-label"
                error={fieldErrors.reasonForHiring}
              >
                {toggleField('reasonForHiring')}
              </Field>
              <Field label="Cost category" labelId="costCategory-label" error={fieldErrors.costCategory}>
                {toggleField('costCategory')}
              </Field>
            </div>

            {/* Below the fields it is derived from and above the rationale — the cost is a
                consequence of the choices just made, and it belongs in front of someone before
                they write the case for the role rather than after. */}
            <CompensationSummary compensation={compensation.first} busy={compensation.isPending} />

            {/* Full width, above the field it points to, rather than under Track and level — a
                paragraph in one grid cell would leave the two columns lopsided (David, Sep 2026). */}
            {backfill && (
              <p className="rounded border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700">{levelNote(backfill)}</p>
            )}

            {/* Directly above the rationale it is argued in: the rating is a claim about where the
                role ranks, and "Why this role?" is where that claim is made. */}
            <PriorityField
              value={form.priorityRating ?? ''}
              onChange={set('priorityRating')}
              options={options?.priorityRating}
              disabled={!options}
              error={fieldErrors.priorityRating}
            />

            <Field label="Why this role?" htmlFor="proposalRational" required error={fieldErrors.proposalRational}>
              <textarea
                id="proposalRational"
                rows={4}
                className={FIELD_CLASS}
                value={form.proposalRational}
                onChange={(e) => set('proposalRational')(e.target.value)}
                placeholder="What this role unblocks, and what happens without it."
              />
            </Field>

            {mutation.error && !(mutation.error instanceof ProposalValidationError) && (
              <p className="text-xs text-red-600">
                {mutation.error instanceof Error ? mutation.error.message : 'Could not save the proposal.'}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              {!backfill && (
                <Button type="button" variant="secondary" size="sm" disabled={blocked} onClick={goBulk}>
                  Propose in bulk
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                loading={mutation.isPending}
                disabled={blocked || mutation.isPending}
              >
                Propose role
              </Button>
            </div>
          </form>
        )}
      </ModalContent>
    </Modal>
  );
}
